<template>
  <div class="revenue-analytics">
    <div v-if="loading" class="text-center py-5">
      <a-spin size="large" />
    </div>
    <div v-else>
      <!-- Summary Cards -->
      <div class="row mb-4">
        <div class="col-md-3 col-sm-6 mb-3">
          <div class="stat-card">
            <div class="stat-icon revenue">
              <a-icon type="dollar" />
            </div>
            <div class="stat-content">
              <div class="stat-value">
                {{ formatCurrency(data && data.totalRevenue) }}
              </div>
              <div class="stat-label">
                {{ $t("financialStats.totalRevenue") }}
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-3 col-sm-6 mb-3">
          <div class="stat-card">
            <div class="stat-icon invoices">
              <a-icon type="file-text" />
            </div>
            <div class="stat-content">
              <div class="stat-value">{{ data.totalInvoices }}</div>
              <div class="stat-label">
                {{ $t("financialStats.totalInvoices") }}
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-3 col-sm-6 mb-3">
          <div class="stat-card">
            <div class="stat-icon average">
              <a-icon type="calculator" />
            </div>
            <div class="stat-content">
              <div class="stat-value">
                {{ formatCurrency(data && data.averageInvoiceValue) }}
              </div>
              <div class="stat-label">
                {{ $t("financialStats.averageInvoiceValue") }}
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-3 col-sm-6 mb-3">
          <div class="stat-card">
            <div class="stat-icon growth">
              <a-icon type="rise" />
            </div>
            <div class="stat-content">
              <div class="stat-value">{{ revenueGrowth }}%</div>
              <div class="stat-label">
                {{ $t("financialStats.growthRate") }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="showRevenueBreakdown"
        class="row mb-4"
      >
        <div class="col-md-4 col-sm-6 mb-3">
          <div class="stat-card stat-card--compact">
            <div class="stat-content">
              <div class="stat-value text-success">{{ formatCurrency(data.tuitionRevenue) }}</div>
              <div class="stat-label">{{ $t("financialStats.revenueSource.tuition") }}</div>
            </div>
          </div>
        </div>
        <div class="col-md-4 col-sm-6 mb-3">
          <div class="stat-card stat-card--compact">
            <div class="stat-content">
              <div class="stat-value text-primary">{{ formatCurrency(data.inverseEncaissementRevenue) }}</div>
              <div class="stat-label">{{ $t("financialStats.revenueSource.inverseEncaissement") }}</div>
            </div>
          </div>
        </div>
        <div class="col-md-4 col-sm-6 mb-3">
          <div class="stat-card stat-card--compact">
            <div class="stat-content">
              <div class="stat-value" style="color: #722ed1">{{ formatCurrency(data.avoirFournisseurRevenue) }}</div>
              <div class="stat-label">{{ $t("financialStats.revenueSource.avoirFournisseur") }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Charts Row -->
      <div class="row mb-4">
        <div class="col-lg-8 mb-4">
          <div class="card">
            <div class="card-header">
              <h6 class="mb-0">{{ $t("financialStats.dailyRevenueTrend") }}</h6>
            </div>
            <div class="card-body">
              <apexchart
                :options="dailyChartOptions"
                :series="dailyChartData.series"
                type="line"
                height="300"
                class="chart-container"
              />
            </div>
          </div>
        </div>
        <div class="col-lg-4 mb-4">
          <div class="card">
            <div class="card-header">
              <h6 class="mb-0">{{ $t("financialStats.revenueByType") }}</h6>
            </div>
            <div class="card-body">
              <apexchart
                :options="typeChartOptions"
                :series="typeChartData.series"
                type="donut"
                height="300"
                class="chart-container"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Payment Methods Table -->
      <div class="row">
        <div class="col-12">
          <div class="card">
            <div class="card-header">
              <h6 class="mb-0">
                {{ $t("financialStats.paymentMethodsBreakdown") }}
              </h6>
            </div>
            <div class="card-body">
              <a-table
                :columns="paymentColumns"
                :dataSource="paymentData"
                :pagination="false"
                size="small"
              >
                <template slot="method" slot-scope="text">
                  <a-tag :color="getMethodColor(text)">{{
                    getMethodName(text)
                  }}</a-tag>
                </template>
                <template slot="amount" slot-scope="text">
                  {{ formatCurrency(text) }}
                </template>
                <template slot="percentage" slot-scope="text">
                  <a-progress
                    :percent="parseFloat(text)"
                    :showInfo="false"
                    :strokeWidth="8"
                    :strokeColor="getMethodColorForPercentage(text)"
                  />
                  <span class="ml-2">{{ text }}%</span>
                </template>
              </a-table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { getBuildingCurrency } from "@/utilities/countrySpecific";

export default {
  name: "RevenueAnalytics",
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    data: {
      type: Object,
      default: () => ({
        totalRevenue: 0,
        totalInvoices: 0,
        averageInvoiceValue: 0,
        tuitionRevenue: 0,
        inverseEncaissementRevenue: 0,
        avoirFournisseurRevenue: 0,
        revenueByType: { F: 0, R: 0 },
        revenueByPaymentMethod: {},
        dailyRevenue: [],
      }),
    },
  },
  computed: {
    showRevenueBreakdown() {
      return (
        this.data &&
        (this.data.tuitionRevenue != null ||
          this.data.inverseEncaissementRevenue != null ||
          this.data.avoirFournisseurRevenue != null)
      );
    },
    revenueGrowth() {
      // Calculate growth rate based on daily revenue
      if (
        !this.data ||
        !this.data.dailyRevenue ||
        this.data.dailyRevenue.length < 2
      )
        return 0;

      const sortedRevenue = [...this.data.dailyRevenue].sort(
        (a, b) => new Date(a.date) - new Date(b.date)
      );
      const firstHalf = sortedRevenue.slice(
        0,
        Math.floor(sortedRevenue.length / 2)
      );
      const secondHalf = sortedRevenue.slice(
        Math.floor(sortedRevenue.length / 2)
      );

      const firstHalfAvg =
        firstHalf.reduce((sum, item) => sum + item.amount, 0) /
        firstHalf.length;
      const secondHalfAvg =
        secondHalf.reduce((sum, item) => sum + item.amount, 0) /
        secondHalf.length;

      if (firstHalfAvg === 0) return 0;
      return (((secondHalfAvg - firstHalfAvg) / firstHalfAvg) * 100).toFixed(1);
    },

    dailyChartData() {
      if (
        !this.data ||
        !this.data.dailyRevenue ||
        this.data.dailyRevenue.length === 0
      ) {
        return {
          series: [],
        };
      }

      const sortedRevenue = [...this.data.dailyRevenue].sort(
        (a, b) => new Date(a.date) - new Date(b.date)
      );
      const labels = sortedRevenue.map((item) =>
        moment(item.date).format("MMM DD")
      );
      const series = [sortedRevenue.map((item) => item.amount)];

      return {
        labels,
        series: [
          { name: this.$t("financialStats.dailyRevenue"), data: series[0] },
        ],
      };
    },

    typeChartData() {
      const revenueByType = (this.data && this.data.revenueByType) || {};
      const F = revenueByType.F || 0;
      const R = revenueByType.R || 0;
      const total = F + R;

      if (total === 0) {
        return {
          series: [],
        };
      }

      return {
        series: [F, R],
        labels: [
          this.$t("financialStats.factures"),
          this.$t("financialStats.receipts"),
        ],
      };
    },

    paymentData() {
      const methods = (this.data && this.data.revenueByPaymentMethod) || {};
      const total = Object.values(methods).reduce(
        (sum, amount) => sum + amount,
        0
      );

      return Object.entries(methods)
        .map(([method, amount]) => ({
          method,
          amount,
          percentage: total > 0 ? ((amount / total) * 100).toFixed(1) : 0,
        }))
        .sort((a, b) => b.amount - a.amount);
    },

    paymentColumns() {
      return [
        {
          title: this.$t("financialStats.paymentMethod"),
          dataIndex: "method",
          key: "method",
          scopedSlots: { customRender: "method" },
        },
        {
          title: this.$t("financialStats.amount"),
          dataIndex: "amount",
          key: "amount",
          scopedSlots: { customRender: "amount" },
        },
        {
          title: this.$t("financialStats.percentage"),
          dataIndex: "percentage",
          key: "percentage",
          scopedSlots: { customRender: "percentage" },
        },
      ];
    },

    dailyChartOptions() {
      return {
        chart: {
          type: "line",
          height: 300,
          toolbar: {
            show: true,
          },
        },
        colors: ["#1890ff"],
        stroke: {
          curve: "smooth",
          width: 3,
        },
        fill: {
          type: "gradient",
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.7,
            opacityTo: 0.3,
            stops: [0, 90, 100],
          },
        },
        markers: {
          size: 4,
          hover: {
            size: 6,
          },
        },
        xaxis: {
          categories: this.dailyChartData.labels || [],
          labels: {
            style: {
              colors: "#8c8c8c",
              fontSize: "12px",
            },
          },
        },
        yaxis: {
          labels: {
            formatter: (value) => {
              if (value >= 1000000) {
                return (value / 1000000).toFixed(1) + "M";
              } else if (value >= 1000) {
                return (value / 1000).toFixed(1) + "K";
              }
              return value;
            },
            style: {
              colors: "#8c8c8c",
              fontSize: "12px",
            },
          },
        },
        tooltip: {
          y: {
            formatter: (value) => {
              return value.toLocaleString() + ` ${this.currency}`;
            },
          },
        },
        legend: {
          show: false,
        },
        grid: {
          borderColor: "#f0f0f0",
          strokeDashArray: 3,
        },
      };
    },

    typeChartOptions() {
      return {
        chart: {
          type: "donut",
          height: 300,
        },
        colors: ["#52c41a", "#1890ff"],
        labels: this.typeChartData.labels || [],
        dataLabels: {
          enabled: true,
          formatter: (val) => {
            return Number(val).toFixed(1) + "%";
          },
        },
        plotOptions: {
          pie: {
            donut: {
              size: "60%",
            },
          },
        },
        tooltip: {
          y: {
            formatter: (value) => {
              return value.toLocaleString() + ` ${this.currency}`;
            },
          },
        },
        legend: {
          position: "bottom",
        },
      };
    },
  },
  data() {
    return {
      currency: null,
    };
  },
  created() {
    this.currency = getBuildingCurrency();
  },
  methods: {
    formatCurrency(value) {
      if (!value && value !== 0) {
        return `0 ${this.currency}`;
      }
      if (value >= 1000000) {
        return (value / 1000000).toFixed(1) + `M ${this.currency}`;
      } else if (value >= 1000) {
        return (value / 1000).toFixed(1) + `K ${this.currency}`;
      }
      return value.toLocaleString() + ` ${this.currency}`;
    },

    getMethodColorForPercentage(percentage) {
      const payment = this.paymentData.find((p) => p.percentage === percentage);
      return payment ? this.getMethodColor(payment.method) : "#8c8c8c";
    },

    getMethodName(method) {
      return this.$t("recette.paymentMethod." + method);
    },

    getMethodColor(method) {
      const colors = {
        ESPESE: "green",
        CHEQUE: "blue",
        TPE: "orange",
        TRAITE: "purple",
        VIREMENT: "red",
      };
      return colors[method] || "default";
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../styles/shared.scss';

.revenue-analytics {
  .chart-container {
    :global(.ct-series-a) {
      .ct-line,
      .ct-point {
        stroke: #52c41a;
      }
      .ct-area {
        fill: #52c41a;
        fill-opacity: 0.1;
      }
    }
  }
}
</style>
